import apiClient from '../client';
import ENDPOINTS from '../endpoints';

/* A donation's `userId` is whichever id the donor has. The picker offers the
   Parents-page roster (see roomParentsService.getParents), where a person may
   hold an app account (userId) and/or a roster record (parentId). The app
   account wins because the server resolves the donor's name and photo by
   looking `userId` up in users; an email-only parent has no such account, so
   their roster id is stored and the CMS names them from the roster instead. */
export const donorIdOf = (parent) => parent?.userId || parent?.parentId || parent?.id || '';

/* The roster row a stored id points at — either of the two ids above, or an
   auth0 id such as a donation/expense's recorder (createdBy). */
export const findParentById = (parents, id) =>
  (id && parents.find((p) => p.userId === id || p.parentId === id || p.id === id)) || null;

/* Donations arrive from /roomParentAdmin/donation — tolerate both a flat doc
   and one carrying a populated user. dateReceived is a full ISO timestamp:
   trim to yyyy-mm-dd for fmtDate/sorting. */
function normalizeDonation(d) {
  const user = d?.user || d?.parent || d?.donor || null;
  const meta = user?.user_metadata || d?.user_metadata || {};
  return {
    id: d?._id || d?.id || '',
    userId: d?.userId || d?.user_id || user?.userId || user?.user_id || '',
    parentName: d?.parentName || d?.userName || meta.name || user?.name || '',
    parentPicture: meta.profilePic || user?.profilePic || d?.profilePic || null,
    amount: Number(d?.amount) || 0,
    source: d?.source || '',
    date: String(d?.dateReceived || d?.date || d?.createdAt || '').slice(0, 10),
    reason: d?.reason || '',
    note: d?.note || '',
    /* the room parent who recorded it — the API returns the id only */
    loggedById: d?.createdBy || '',
    status: d?.status || 'active',
  };
}

/* Expenses come from /roomParentAdmin/expense (own records only). */
function normalizeExpense(e) {
  return {
    id: e?._id || e?.id || '',
    desc: e?.usedFor || e?.description || e?.desc || '',
    category: e?.category || e?.tag || '',
    amount: Number(e?.amount) || 0,
    date: String(e?.dateSpent || e?.date || e?.createdAt || '').slice(0, 10),
    receipt: e?.receipt || e?.receiptUrl || e?.receiptPath || e?.imagePath || null,
    /* on an expense `userId` IS the room parent who logged it, not a donor */
    loggedById: e?.userId || '',
    status: e?.status || 'active',
  };
}

/* Add/edit expense are multipart (the receipt is a file) — axios lets the
   browser set the boundary when the body is FormData. */
function expenseFormData(fields, receiptFile) {
  const fd = new FormData();
  Object.entries(fields).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') fd.append(k, v);
  });
  if (receiptFile) fd.append('receipt', receiptFile);
  return fd;
}

const fundraisingService = {
  /* Paginated per network; omit status to get active + inactive. */
  listDonations: async (networkId, { page = 1, limit = 10, status = 'active' } = {}) => {
    const res = await apiClient.get(ENDPOINTS.ROOM_PARENT_ADMIN.DONATIONS_BY_NETWORK(networkId), {
      params: { page, limit, ...(status ? { status } : {}) },
    });
    const data = res?.data ?? res;
    const rawList =
      data?.donations || data?.docs || data?.items || data?.results || data?.list || (Array.isArray(data) ? data : []);
    const total = Number(data?.total ?? data?.totalCount ?? data?.count ?? data?.pagination?.total ?? rawList.length);
    const totalPages = Number(
      data?.totalPages ?? data?.pages ?? data?.pagination?.totalPages ?? Math.max(1, Math.ceil(total / limit))
    );
    return { donations: rawList.map(normalizeDonation), total, totalPages };
  },

  /* { networkId, userId, amount, source, dateReceived, reason, note } */
  addDonation: (donation) => apiClient.post(ENDPOINTS.ROOM_PARENT_ADMIN.DONATION, donation),

  /* { id, ...only what changed }; { id, status: 'inactive' } deactivates. */
  updateDonation: (patch) => apiClient.put(ENDPOINTS.ROOM_PARENT_ADMIN.DONATION, patch),

  /* Paginated per network; omit status to get active + inactive. */
  listExpenses: async (networkId, { page = 1, limit = 10, status = 'active' } = {}) => {
    const res = await apiClient.get(ENDPOINTS.ROOM_PARENT_ADMIN.EXPENSES_BY_NETWORK(networkId), {
      params: { page, limit, ...(status ? { status } : {}) },
    });
    const data = res?.data ?? res;
    const rawList =
      data?.expenses || data?.docs || data?.items || data?.results || data?.list || (Array.isArray(data) ? data : []);
    const total = Number(data?.total ?? data?.totalCount ?? data?.count ?? data?.pagination?.total ?? rawList.length);
    const totalPages = Number(
      data?.totalPages ?? data?.pages ?? data?.pagination?.totalPages ?? Math.max(1, Math.ceil(total / limit))
    );
    return { expenses: rawList.map(normalizeExpense), total, totalPages };
  },

  /* fields: { networkId, category, amount, dateSpent, usedFor }; receiptFile optional. */
  addExpense: (fields, receiptFile) =>
    apiClient.post(ENDPOINTS.ROOM_PARENT_ADMIN.EXPENSE, expenseFormData(fields, receiptFile)),

  /* fields: { id, ...only what changed }; { id, status: 'inactive' } deactivates;
     a new receiptFile replaces the stored receipt URL. */
  updateExpense: (fields, receiptFile) =>
    apiClient.put(ENDPOINTS.ROOM_PARENT_ADMIN.EXPENSE, expenseFormData(fields, receiptFile)),
};

export default fundraisingService;
